<template>
  <div>
    <section-title content="Configuración de Secciones" has-line />
    <div class="my-4">
      <validation-observer ref="sectionValidator">
        <div class="row">
          <div class="col-12 col-lg-8">
            <div class="">
              <div class="row d-flex align-items-end">
                <div class="col-12 col-md-4">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <b>Nombre de la Sección:</b>
                    <custom-drop-down
                      v-model="model.section"
                      custom-error-msg="El campo es requerido"
                      :disabled="isDragAndDropEnable"
                      :error="errors[0]"
                      fieldkey="id"
                      fieldname="name"
                      :options="availableSections"
                      placeholder="Seleccione"
                    />
                  </validation-provider>
                </div>
                <div v-if="isOther" class="col-12 col-md-3 pl-0">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <custom-input
                      v-model="model.other"
                      class="mt-2"
                      custom-error-msg="El campo es requerido"
                      :error="errors[0]"
                      placeholder="Escriba una Sección"
                    />
                  </validation-provider>
                </div>
                <action-button
                  :disabled="isDragAndDropEnable"
                  i="fas fa-file-plus"
                  text="Añadir Sección"
                  variant="success col-12 col-md-2 px-0"
                  @click="add()"
                />
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <div class="d-flex justify-content-lg-end mt-lg-4">
              <div class="mr-1">
                <action-button
                  :class="
                    isDragAndDropEnable
                      ? 'custom-reorganize-btn text-white'
                      : 'outline-dark-gray'
                  "
                  i="far fa-sort-alt"
                  :text="
                    isDragAndDropEnable
                      ? 'Finalizar Reorganización'
                      : 'Reorganizar Secciones'
                  "
                  @click="reorganize()"
                />
              </div>
              <div class="mx-1">
                <action-button
                  class="outline-dark-gray"
                  :disabled="isDragAndDropEnable"
                  :i="
                    allExpanded
                      ? 'fal fa-compress-arrows-alt'
                      : 'fal fa-expand-alt'
                  "
                  :text="allExpanded ? 'Colapsar Todo' : 'Expandir Todo'"
                  @click="toggleExpandCollapse"
                />
              </div>
            </div>
          </div>
        </div>
      </validation-observer>
    </div>
    <div class="col-12">
      <custom-collapse id="rubric-sections-collapse">
        <draggable
          :disabled="!isDragAndDropEnable"
          force-fallback
          ghost-class="ghost"
          :list="input"
          @end="dragging = false"
          @start="dragging = true"
        >
          <custom-collapse-item
            v-for="(item, key) in input"
            :id="`section-collapse-item-${key + 1}`"
            :key="`section-collapse-item-${key + 1}`"
            :ref="`section-collapse-item-${key + 1}`"
            :auto-expand-collapse-enabled="false"
            button-icon="far fa-times-circle color-red"
            class="my-3 collapse-item section bg-gray"
            :class="{ 'rounded invalid': item.valid === false }"
            :disabled="isReadOnly || isDragAndDropEnable"
            :expanded="item.expanded"
            expanded-icon="fas fa-plus-circle custom-reorganize-btn"
            icon-class-when-button-disabled="fas fa-grip-vertical flex-fill d-flex justify-content-end align-items-center"
            :keep-btn-visible="false"
            parent-id="rubric-sections-collapse"
            read-only-icon="fas fa-plus-circle color-gray-2"
            :score="`Puntaje: ${score(item)}`"
            score-class="text-muted mr-2"
            :show-icon-when-button-disabled="isDragAndDropEnable"
            :show-score="isScoreVisible"
            :title="item.name"
            @actionButtonClick="quit(item, key)"
            @expandButtonClick="expand(item, key)"
          >
            <template #title-left>
              <span class="title-number-box rounded ml-2 p-1 text-white">
                {{ key + 1 }}
              </span>
            </template>

            <div class="section-title">
              <div class="d-inline mr-2 bg-white">
                <i class="fas fa-list text-orange fa-lg" />
              </div>
              <span class="color-gray-7 font-16 font-weight-bold">
                Información de Sección
              </span>
            </div>

            <questions
              :ref="`section-${key}`"
              v-model="item.questions"
              :answers="answers"
              @add="(q) => addQuestion(q, item)"
              @quit="(q) => quitQuestion(q, item)"
              @validate="onValidateQuestions(item)"
            />
          </custom-collapse-item>
        </draggable>
      </custom-collapse>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomDropDown from 'custom-drop-down';
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import CustomCollapse from '@/components/common/CustomCollapse.vue';
import CustomCollapseItem from '@/components/common/CustomCollapseItem.vue';
import { getSections } from '@/modules/rubric/api.js';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import draggable from 'vuedraggable';
import Questions from './Questions';
import RubricStatus from '@/utils/shared/constants/rubricStatus.js';

export default {
  name: 'Sections',
  components: {
    SectionTitle,
    CustomDropDown,
    ActionButton,
    CustomInput,
    CustomCollapse,
    CustomCollapseItem,
    draggable,
    Questions,
  },
  mixins: [alertMixin],
  props: {
    input: {
      type: Array,
      default: () => [],
    },
    answers: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    status: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      model: {
        section: null,
        other: null,
      },
      isDragAndDropEnable: false,
      sections: [],
      totalScore: 0,
    };
  },
  computed: {
    draggingInfo() {
      return this.dragging ? 'under drag' : '';
    },
    allExpanded() {
      return this.input.every((item) => item.expanded);
    },
    isOther() {
      return this.model.section?.id === -1;
    },
    availableSections() {
      return this.sections.filter(
        (x) => !this.input.map((x) => x.id).includes(x.id)
      );
    },
    isScoreVisible() {
      return !this.status?.id || this.status.id === RubricStatus.Draft;
    },
    score() {
      return (s) => {
        const maxs = s.questions?.map(({ answers }) =>
          answers?.length ? Math.max(...answers.map((x) => x.value)) : 0
        );
        s.score = maxs?.length ? maxs.reduce((total, max) => total + max) : 0;
        return s.score;
      };
    },
  },
  async mounted() {
    await this.getAllSections();
  },
  methods: {
    reorganize() {
      this.isDragAndDropEnable = !this.isDragAndDropEnable;
      this.input.forEach((item) => (item.expanded = false));
    },
    toggleExpandCollapse() {
      const expand = !this.allExpanded;
      this.input.forEach((item) => (item.expanded = expand));
    },
    async getAllSections() {
      const { data } = await getSections();
      this.sections = data;
      this.sections.push({ id: -1, name: 'Otro' });
    },
    resetForm() {
      this.$refs.sectionValidator.reset();
      this.model.other = null;
      this.model.section = null;
    },
    async add() {
      let valid = await this.$refs.sectionValidator.validate();
      if (!valid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }
      valid = await this.validate();
      if (!valid) {
        this.ShowToast(
          'Alerta',
          'Es necesario documentar las secciones agregadas.',
          'error'
        );
        return;
      }

      const item = {
        id: this.isOther ? null : this.model.section.id,
        name: this.isOther ? this.model.other : this.model.section.name,
        questions: [],
        valid: true,
        expanded: false,
        score: 0,
      };
      this.input.push(item);
      this.resetForm();

      this.expand(item, this.input.length - 1);
      this.$emit('add', item);
      this.$emit('add:section', item);
    },
    expandAll(expanded = true) {
      this.input.forEach((x) => (x.expanded = expanded));
    },
    expand(item, key) {
      if (this.isDragAndDropEnable) return;
      this.input
        .filter((x, i) => i !== key)
        .forEach((x) => (x.expanded = false));
      setTimeout(() => (item.expanded = !item.expanded), 100);
    },
    async quit(item, key) {
      if (this.isDragAndDropEnable) return;
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea remover la sección <strong>${item.name}</strong>, se eliminarán los datos suministrados hasta ahora?`,
        'Si',
        'No'
      );

      if (!answer) return;
      this.input.splice(key, 1);
      this.$emit('quit', item);
      this.$emit('quit:section', item);
    },
    async validate() {
      if (!this.input.length) return true;
      const forms = this.input.map((item, key) => {
        const validator = this.$refs[`section-${key}`][0];
        return validator.validate().then((val) => (item.valid = val));
      });
      await Promise.all(forms);
      return this.input.every((x) => x.valid);
    },
    addQuestion(item, section) {
      this.$emit('add:question', item, section);
    },
    quitQuestion(item, section) {
      this.$emit('quit:question', item, section);
    },
    onValidateQuestions(item) {
      item.valid = item.questions.every((x) => x.valid);
    },
  },
};
</script>

<style scoped lang="scss">
.outline-dark-gray {
  color: #3f5b60;
  background-color: #ffffff;
  border-color: #3f5b60;
}

.collapse-item ::v-deep .card-header {
  padding: 0.75rem 1rem !important;
}

.collapse-item ::v-deep .tag-badge-pei {
  background: none;
}

.collapse-item.section ::v-deep .shadow2 {
  -webkit-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  box-shadow: none !important;
  background-color: #f7f7f7;
}
.collapse-item.section ::v-deep .card-header {
  border-radius: 0.25rem !important;
  border-bottom: 2px solid #e6e6e6;
}
.collapse-item.section ::v-deep .card-header .title-number-box {
  background-color: #2db782;
}

.collapse-item.invalid.section {
  border: 1px solid #ff0018 !important;
}
.collapse-item.invalid.section ::v-deep .card-header .title-number-box {
  background-color: #ff5b5c !important;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.custom-reorganize-btn {
  background-color: #405c64;
}
.section-title > div {
  border: 3px solid white;
  padding: 6px 7px;
  border-radius: 30px;
  -webkit-box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.1607843137);
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.1607843137);
}
</style>
